import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Users, List, BarChart3, Database, ShieldCheck, Zap, TrendingUp, Settings } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Guia de Gestão de Clientes IPTV", href: "/blog/guia-gestao-clientes-iptv" }
  ];

  const sections = [
    {
      title: "1. Organização: A Espinha Dorsal da Escala",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Muitos revendedores conseguem gerir 20 ou 30 clientes usando apenas a memória ou anotações esparsas no WhatsApp. No entanto, quando esse número sobe para 100, 200 ou 1.000, o caos se instala. Gestão de clientes em IPTV não é apenas anotar quem pagou; é ter controle total sobre vencimentos, comportamentos de uso e histórico de suporte.
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Uma gestão eficiente permite que você saiba exatamente qual cliente está prestes a sair, quem são seus maiores indicadores e onde estão os problemas técnicos recorrentes que podem estar matando seu lucro. Sem organização, você gasta mais tempo "apagando incêndios" do que crescendo.
          </p>
        </div>
      )
    },
    {
      title: "2. Ferramentas de Gestão (CRM e Painel)",
      type: "featured" as const,
      content: (
        <div className="space-y-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Settings className="w-6 h-6 text-electric" /> Domínio do Painel P2 PRO</h4>
              <p className="text-sm text-muted-foreground">O seu painel de revenda é o seu banco de dados primário. Aprenda a usar as funções de etiquetas (tags), observações e categorias para separar clientes por planos, dispositivos ou data de entrada.</p>
            </div>
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Database className="w-6 h-6 text-amber-500" /> CRM Complementar</h4>
              <p className="text-sm text-muted-foreground">Para operações maiores, utilize um CRM simples (como Trello, Notion ou ferramentas específicas de IPTV) para acompanhar o 'status' de cada lead, desde o primeiro teste até a renovação anual.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "3. O Ciclo de Vida do Cliente IPTV",
      content: (
        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-muted-foreground">Uma boa gestão acompanha o cliente em todas as suas fases:</p>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">1</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Onboarding (Boas-vindas)</h4>
              <p className="text-muted-foreground">Gestão não é só controle. Assim que o cliente assina, ele deve entrar em um fluxo de automação que envia o guia de instalação, as regras do serviço e os contatos de suporte.</p>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">2</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Monitoramento Proativo</h4>
              <p className="text-muted-foreground">Verifique periodicamente no painel se o cliente está conseguindo logar. Se ele não loga há 5 dias, entre em contato. Antecipar-se ao problema é a melhor forma de gestão de retenção.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "4. Gestão Financeira e Cobrança",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-muted-foreground mb-6">O maior gargalo de tempo na gestão de clientes é a cobrança. Resolva isso com método:</p>
          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <List className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Régua de Cobrança Automática</h5>
                <p className="text-sm text-muted-foreground">Defina prazos: Lembrete 3 dias antes, 1 dia antes e aviso de suspensão no dia do vencimento. Use mensagens cordiais mas profissionais.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <BarChart3 className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Análise de Receita (MRR)</h5>
                <p className="text-sm text-muted-foreground">Saiba exatamente quanto de receita recorrente mensal você tem. Isso permite que você planeje novos investimentos em marketing com segurança.</p>
              </div>
            </li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="relative">
      <SEOPage
        category="Guias IPTV"
        title="Guia de Gestão de Clientes IPTV: Do Caos à Escala"
        subtitle="Aprenda as melhores práticas e ferramentas para gerenciar centenas de clientes com facilidade, reduzindo o churn e aumentando sua lucratividade."
        heroImage="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=1200"
        introParagraphs={[
          "Se você quer ter um negócio de verdade e não apenas uma renda extra instável, você precisa dominar a gestão de clientes. No modelo de recorrência do IPTV, a organização é o que separa os amadores que trabalham muito e ganham pouco, dos profissionais que escalam sua operação com processos claros e eficientes.",
          "Neste guia técnico de gestão, vamos explorar como organizar sua base de dados, quais métricas acompanhar diariamente e como automatizar as tarefas repetitivas que tomam o seu tempo (como cobranças e suporte básico). Uma boa gestão é invisível para o cliente, mas é sentida através de um serviço que funciona e de um atendimento que nunca falha.",
          "Prepare-se para profissionalizar seu fluxo de trabalho. Vamos transformar sua lista de contatos em uma verdadeira carteira de ativos recorrentes. A escala começa pela organização."
        ]}
        breadcrumbs={breadcrumbs}
        faq={[
          {
            question: "Qual o melhor sistema para gerir vencimentos?",
            answer: "O próprio painel da P2 PRO já oferece ferramentas nativas para isso. Para algo mais avançado, muitos revendedores utilizam gestores de assinaturas ou bots integrados ao WhatsApp."
          },
          {
            question: "Como lidar com inadimplência no IPTV?",
            answer: "No IPTV, o acesso é pré-pago ou renovado mediante pagamento. Não houve pagamento, o sinal é suspenso automaticamente pelo painel. Isso reduz a inadimplência a quase zero."
          }
        ]}
        benefits={[
          {
            title: "Tranquilidade Operacional",
            description: "Saiba exatamente o que precisa ser feito a cada dia sem esquecer de nenhum cliente.",
            icon: <ShieldCheck className="w-6 h-6" />
          },
          {
            title: "Previsibilidade Financeira",
            description: "Tenha clareza sobre seu faturamento futuro baseado no controle rigoroso da sua base ativa.",
            icon: <TrendingUp className="w-6 h-6" />
          }
        ]}
        howItWorks={[
          {
            title: "Mapeamento da Carteira",
            description: "Organize seus clientes atuais por data de vencimento, plano e canal de aquisição."
          },
          {
            title: "Automação de Processos",
            description: "Implemente lembretes e fluxos de suporte que funcionem de forma automática ou semi-automática."
          }
        ]}
        diferenciais={[
          {
            title: "Infraestrutura P2 PRO",
            description: "Nosso painel foi desenhado para facilitar a vida do revendedor, com funções de gestão simplificadas e poderosas.",
            icon: <Zap className="w-6 h-6" />
          }
        ]}
        sections={sections}
        ctaTitle="📊 Quer Gerenciar sua Revenda como um Profissional?"
        ctaDescription="Aproveite a tecnologia de ponta do painel P2 PRO e as melhores táticas de gestão para levar seu faturamento para o próximo nível com total controle."
        ctaButtonText="Quero me Tornar um Revendedor de Elite"
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/guia-gestao-clientes-iptv")({
  head: () => ({
    meta: [
      { title: "Guia de Gestão de Clientes IPTV: Do Caos à Escala" },
      { name: "description", content: "Manual completo de gestão de clientes para revenda IPTV. Aprenda a organizar sua base, automatizar cobranças e utilizar métricas de retenção para crescer seu negócio." },
    ],
  }),
  component: ArticlePage,
});